import { inject, Injectable, signal } from "@angular/core";
import { ProposalDocumentUpload, ProposalScope } from "@app/core/domain/proposal-status";
import {
  CategoryRequirement,
  FileToUpload,
  SubmitInfo,
  SubmitInfoCourse,
  SubmitInfoMinor,
  SubmitInfoMinorExamComponent,
} from "@app/core/domain/submit-proposal";
import { PlanService } from "@app/core/services/plan.service";
import { SubmitProposalService } from "@app/core/services/submit-proposal.service";
import { CaciUtil } from "@app/core/util/caci-util";
import {
  SubmitChoiceCourseListBuilder,
  SubmitCourseGroup,
} from "@app/features/submit-proposal/services/submit-choice-course-list.builder";
import { GenericDataListItem } from "@app/shared/components/generic-data-table/generic-data-table.component";
import { LanguageService } from "@app/shared/services/language.service";
import { LoadingService } from "@app/shared/services/loading-service";
import { PlanActionService } from "@app/shared/services/plan-action.service";
import { CourseHelper } from "@core/domain/helpers/course-helper";
import { StatusMessage, StatusMessageType } from "@core/domain/status-message";
import { TranslateService } from "@ngx-translate/core";
import { CourseDataMapper } from "@shared/services/course-data-mapping.service";
import { PlanStateService } from "@shared/services/plan-state.service";
import { map, Observable, of, switchMap, tap } from "rxjs";

export enum SubmitProposalStep {
  Choice = "choice",
  Confirm = "confirm",
}

@Injectable({
  providedIn: "root",
})
export class SubmitProposalStateService {
  // minorsInProposal keeps track of selected minor courses, grouped by minor
  // (used when the SVO places the exam components)
  minorsInProposal: MinorInProposal[] = [];
  // minorComponentsInProposal keeps track of selected minor courses, grouped by minor component
  // (used when the student places the exam components)
  minorComponentsInProposal: MinorComponentInProposal[] = [];
  // electivesCoursesByCategory keeps track of selected electives courses (including minor courses), grouped by category
  // (public for unit tests)
  categoriesInProposal: CategoryInProposal[] = [];

  electivesCourseGroups: SubmitCourseGroup[] = [];
  otherCourseGroups: SubmitCourseGroup[] = []; // when submitting the whole program

  submitInfo = signal<SubmitInfo | undefined>(undefined);
  planAllowsChangeExamComponent = signal(false);
  messages = signal<StatusMessage[]>([]);
  studentExplanation: string | undefined;
  filesToUpload: FileToUpload[] = [];
  submitDisabled = true;

  // totalPoints is public for unit tests
  readonly totalPoints = signal(0);
  // selectedPoints is public for unit tests
  readonly selectedElectivesPoints = signal(0);
  // fromLessThanMinToMaxOrMoreWith1Course is true when the user had less than the minimum points selected,
  // then selected one course and reached or exceeded the maximum points
  // (public for unit tests)
  fromLessThanMinToMaxOrMoreWith1Course = false;
  // maxPointsReached is true when the user has selected enough courses to reach the maximum points,
  // or if there is no maximum defined, to reach the minimum points
  private readonly maxPointsReached = signal(false);
  // maxPointsExceeded is true when the user has selected more courses than the maximum points,
  // or if there is no maximum defined, than the minimum points
  private readonly maxPointsExceeded = signal(false);
  private step = SubmitProposalStep.Choice;

  private readonly courseDataMapper = inject(CourseDataMapper);
  private readonly courseHelper = inject(CourseHelper);
  private readonly loadingService = inject(LoadingService);
  private readonly planStateService = inject(PlanStateService);
  private readonly planActionService = inject(PlanActionService);
  private readonly translate = inject(TranslateService);
  private readonly submitProposalService = inject(SubmitProposalService);
  private readonly planService = inject(PlanService);
  private readonly languageService = inject(LanguageService);
  private readonly choiceCourseListBuilder = new SubmitChoiceCourseListBuilder(
    this.translate,
    this.courseDataMapper,
    this,
  );

  initSubmitInfo(submitInfo: SubmitInfo, step: SubmitProposalStep): void {
    this.resetState();
    this.step = step;
    const plan = this.planStateService.currentSelectedPlan()!;
    this.planAllowsChangeExamComponent.set(this.planStateService.currentSelectedPlan()!.allowsChangeExamComponent);
    submitInfo.proposalScope = plan.proposalScope;

    if (submitInfo.electives.categoryRequirements.length) {
      submitInfo.electives.categoryRequirements.forEach((categoryRequirement) => {
        this.addCategory(categoryRequirement.categoryCode);
      });
    }
    // Add categories of courses listed on the right side
    this.addElectivesCourseCategories(submitInfo);

    this.totalPoints.set(submitInfo.pointsPlannedOutsideElectives);

    if (this.planAllowsChangeExamComponent()) {
      // group minor courses by minor component
      this.setMinorComponentsInProposal(submitInfo);
    } else {
      // group minor courses by minor
      this.setMinorsInProposal(submitInfo);
    }

    submitInfo.electives.individualArrangements.forEach((course: SubmitInfoCourse) =>
      this.countPointsOfAutomaticallySelectedCourse(course),
    );

    // build the course groups after setting the planAllowsChangeExamComponent
    // because the course groups are built differently based on these values,
    // and build the course groups before buildMessages,
    // because if the plan allows changing the exam component, buildCourseList ensures the selected points are counted
    this.electivesCourseGroups = this.choiceCourseListBuilder.buildElectivesCourseGroups(submitInfo);
    if (submitInfo.proposalScope === ProposalScope.WHOLE_PROGRAM) {
      this.otherCourseGroups = this.choiceCourseListBuilder.buildOtherCourseGroups(submitInfo);
    }

    // build the messages after counting the points of the individual arrangements,
    // and after building the course groups (which counts the points of the courses already placed in the electives)
    this.buildMessages(submitInfo);

    // set the submitInfo signal last, because it can trigger effects
    this.submitInfo.set(submitInfo);
  }

  changeStepTo(step: SubmitProposalStep): void {
    this.step = step;
    this.messages.set([]);
    if (this.submitInfo()) {
      // const submitInfo needed to avoid false positive in SonarQube server
      const submitInfo = this.submitInfo()!;
      this.buildMessages(submitInfo);
    }
  }

  addElectivesCourseCategories(submitInfo: SubmitInfo): void {
    submitInfo.electives.courses.forEach((electivesCourse: SubmitInfoCourse) => {
      if (electivesCourse.categoryCode !== undefined) {
        // W 2501 003 - empty string is a valid category, those courses must also be counted
        this.addCategory(electivesCourse.categoryCode);
      }
    });

    submitInfo.electives.minors.forEach((minor) => {
      minor.examComponents.forEach((minorComponent) => {
        minorComponent.courses.forEach((minorCourse) => {
          if (minorCourse.categoryCode !== undefined) {
            // W 2501 003 - empty string is a valid category, those courses must also be counted
            this.addCategory(minorCourse.categoryCode);
          }
        });
      });
    });
  }

  isNextStepAllowed(): boolean {
    const submitInfoWithSelections = this.submitInfo()!;
    const miniumumSelectedPointsOK = this.selectedElectivesPoints() >= submitInfoWithSelections.electives.minimumPoints;
    return miniumumSelectedPointsOK && !this.submitBlockedByValidationErrors(submitInfoWithSelections);
  }

  public countPointsOfAutomaticallySelectedCourse(course: SubmitInfoCourse): void {
    const points: number = this.courseHelper.getCoursePoints(course);

    // Update total points to inform other components
    this.totalPoints.set(CaciUtil.roundStudyPoints(this.totalPoints() + points));
    this.selectedElectivesPoints.set(CaciUtil.roundStudyPoints(this.selectedElectivesPoints() + points));
    // Update the category points
    this.categoriesInProposal
      .find((categoryInProposal) => categoryInProposal.code === course.categoryCode)
      ?.addCourseToSelection(course);
  }

  public isCourseSelectable(course: SubmitInfoCourse): boolean {
    // User can select courses as long as the maximum number of points is not reached (or exceeded).
    // If there is no maximum, the minimum counts as maximum.
    // The course with which the maximum is exceeded is the last one allowed to be selected,
    // except for courses with 0 points (they can still be selected when the maximum is reached).
    return !this.maxPointsReached() || this.courseHelper.getCoursePoints(course) === 0;
  }

  /**
   * Is called when the student checks a checkbox for a course in the submit proposal modal.
   * Can happen only when placing is done by the SVO.
   */
  public selectElectivesCourse(submitInfo: SubmitInfo, course: SubmitInfoCourse, minor?: SubmitInfoMinor): void {
    let pointsTotal = this.totalPoints();

    if (minor) {
      const minorInProposal = this.minorsInProposal.find((thisMinorInProposal) => thisMinorInProposal.isEqual(minor));
      if (minorInProposal) {
        if (this.findCourse(minorInProposal.selectedCourses, course.id)) {
          // course is present in minor
          minorInProposal.removeCourseFromSelection(course);
        }
        // course not present in minor
        else if (this.isCourseSelectable(course)) {
          minorInProposal.addCourseToSelection(course);
        }
      }
    }

    let selectedElectivesPoints = this.selectedElectivesPoints();
    this.categoriesInProposal
      .filter((category) => category.code === course.categoryCode)
      .forEach((category) => {
        const points: number = this.courseHelper.getCoursePoints(course);
        if (this.findCourse(category.selectedCourses, course.id)) {
          // course is present in category list, was selected and is now deselected
          category.removeCourseFromSelection(course);
          const selectedElectivesPointsAfterCourseRemoval = CaciUtil.roundStudyPoints(selectedElectivesPoints - points);

          // if fromLessThanMinToMaxOrMoreWith1Course was true,
          // check if this course brings the selected points back down below the max
          if (
            this.fromLessThanMinToMaxOrMoreWith1Course &&
            selectedElectivesPointsAfterCourseRemoval < this.getMaximumToCompareWith(submitInfo)
          ) {
            this.fromLessThanMinToMaxOrMoreWith1Course = false;
          }

          selectedElectivesPoints = selectedElectivesPointsAfterCourseRemoval;
          pointsTotal -= points;
        } else if (this.isCourseSelectable(course)) {
          // course not present in category list, was not selected yet and is now selected
          category.addCourseToSelection(course);
          const selectedElectivesPointsAfterCourseAddition = CaciUtil.roundStudyPoints(
            selectedElectivesPoints + points,
          );

          // check if this course brings the selected points from less than min to max or more
          if (
            selectedElectivesPoints < submitInfo.electives.minimumPoints &&
            selectedElectivesPointsAfterCourseAddition >= this.getMaximumToCompareWith(submitInfo)
          ) {
            this.fromLessThanMinToMaxOrMoreWith1Course = true;
          }

          selectedElectivesPoints = selectedElectivesPointsAfterCourseAddition;
          pointsTotal += points;
        }
      });

    // Update total points to inform other components
    this.totalPoints.set(CaciUtil.roundStudyPoints(pointsTotal));
    this.selectedElectivesPoints.set(CaciUtil.roundStudyPoints(selectedElectivesPoints));
    this.buildMessages(submitInfo);
  }

  private findCourse(courses: SubmitInfoCourse[], id?: number): SubmitInfoCourse | undefined {
    return courses.find((course) => course.id === id);
  }

  private addCategory(categoryCode: string): void {
    if (!this.categoriesInProposal.some((category) => category.code === categoryCode)) {
      this.categoriesInProposal.push(new CategoryInProposal(categoryCode));
    }
  }

  private getMaximumToCompareWith(submitInfo: SubmitInfo): number {
    return submitInfo.electives.maximumPoints ?? submitInfo.electives.minimumPoints;
  }

  private calculateMaxPointsExceeded(submitInfo: SubmitInfo) {
    this.maxPointsExceeded.set(this.selectedElectivesPoints() > this.getMaximumToCompareWith(submitInfo));
  }

  private calculateMaxPointsReached(submitInfo: SubmitInfo) {
    this.maxPointsReached.set(this.selectedElectivesPoints() >= this.getMaximumToCompareWith(submitInfo));
  }

  /**
   * OB-33125 block the submit when placing via the Planapp when there are validation errors. For whole program proposals block when there are plan level validation errors, and for electives proposals block when there are plan level validation errors for the electives.
   */
  submitBlockedByValidationErrors(submitInfo: SubmitInfo): boolean {
    if (!this.planAllowsChangeExamComponent()) {
      return false; // When placing is done by the SVO, validation errors are not blocking the submit
    }
    // When placing is done by the student, validation errors are blocking the submit
    if (submitInfo.proposalScope === ProposalScope.WHOLE_PROGRAM) {
      return this.planStateService.hasValidationErrorsAtPlanLevel();
    }
    return this.planStateService.hasElectivesValidationErrors();
  }

  setFilesToUpload(files: File[]): void {
    this.filesToUpload = files.map(
      (file) =>
        ({
          file: file,
        }) as FileToUpload,
    );
    this.determineSubmitDisabled();
  }

  // public for unit tests
  buildMessages(submitInfo: SubmitInfo): void {
    this.calculateMaxPointsReached(submitInfo);
    this.calculateMaxPointsExceeded(submitInfo);

    this.messages.set(
      this.step === SubmitProposalStep.Choice ? this.buildMessagesChoice(submitInfo) : this.buildWarningsConfirm(),
    );
  }

  private buildMessagesChoice(submitInfo: SubmitInfo): StatusMessage[] {
    const errorsAndWarnings: StatusMessage[] = [];

    const hasEnoughPoints = this.selectedElectivesPoints() >= submitInfo.electives.minimumPoints;
    if (submitInfo.electives.minorRequirementType && hasEnoughPoints) {
      // (only relevant when placing is done by SVO)
      // This check assumes that through the validation messages the student already knows to include at least one minor of the right type.
      // Now it is checked if the student has selected enough courses to include such a minor.
      const minorOfRequiredTypeSelected = this.minorsInProposal.some(
        (minorInProposal) =>
          minorInProposal.type === submitInfo.electives.minorRequirementType &&
          minorInProposal.totalPoints >= minorInProposal.minimumPoints,
      );
      if (!minorOfRequiredTypeSelected) {
        const msg = this.translate.instant("submitProposal.warningMinorRequirement", {
          type: submitInfo.electives.minorRequirementTypeDescription,
        });
        errorsAndWarnings.push(this.buildWarning(msg));
      }
    }

    if (this.submitBlockedByValidationErrors(submitInfo)) {
      if (submitInfo.proposalScope === ProposalScope.WHOLE_PROGRAM) {
        errorsAndWarnings.push(this.buildError(this.translate.instant("submitProposal.blockingValidationErrors")));
      } else {
        errorsAndWarnings.push(
          this.buildError(
            this.translate.instant("submitProposal.blockingElectiveValidationErrors", {
              electivesName: submitInfo.electives.examComponentName,
            }),
          ),
        );
      }
    } else if (this.planStateService.hasValidationMessages()) {
      errorsAndWarnings.push(this.buildWarning(this.translate.instant("submitProposal.warningValidation")));
    }

    if (this.fromLessThanMinToMaxOrMoreWith1Course || (this.maxPointsReached() && !this.maxPointsExceeded())) {
      const msg = this.translate.instant("submitProposal.warningAllowedCreditsReached", {
        name: submitInfo.electives.examComponentName,
      });
      errorsAndWarnings.push(this.buildWarning(msg));
    }

    if (!this.fromLessThanMinToMaxOrMoreWith1Course && this.maxPointsExceeded()) {
      const msg = this.translate.instant("submitProposal.warningAllowedCreditsExceeded", {
        name: submitInfo.electives.examComponentName,
      });
      errorsAndWarnings.push(this.buildWarning(msg));
    }

    return errorsAndWarnings;
  }

  private buildWarningsConfirm(): StatusMessage[] {
    const warnings: StatusMessage[] = [];

    warnings.push(...this.buildWarningsMinorCoursesChecked());

    return warnings;
  }

  private buildWarningsMinorCoursesChecked() {
    const warnings: StatusMessage[] = [];

    // Works only when submitting the electives, because when submitting the whole program,
    // minorComponentsInProposal is used instead of minorsInProposal
    this.minorsInProposal.forEach((minorInProposal) => {
      if (minorInProposal.totalPoints > 0 && minorInProposal.totalPoints < minorInProposal.minimumPoints) {
        const msg = this.translate.instant("submitProposal.warningMinorNotAllCoursesSelected", {
          minorName: minorInProposal.name,
          electivesName: this.submitInfo()?.electives.examComponentName ?? "",
        });
        warnings.push(this.buildWarning(msg));
      }
    });

    return warnings;
  }

  private buildWarning(message: string): StatusMessage {
    return {
      message,
      code: 0,
      type: StatusMessageType.WARNING,
    };
  }

  private buildError(message: string): StatusMessage {
    return {
      message,
      code: 0,
      type: StatusMessageType.ERROR,
    };
  }

  isUploadDocumentRequired(): boolean {
    return this.planStateService.currentSelectedPlan()!.proposalDocumentToUpload === ProposalDocumentUpload.REQUIRED;
  }

  determineSubmitDisabled(): void {
    // explanation is always required
    const hasExplanation = !!this.studentExplanation?.length;
    // sometimes documents are also required
    const requiredFilesUploaded = this.isUploadDocumentRequired() ? !!this.filesToUpload.length : true;

    this.submitDisabled = !(hasExplanation && requiredFilesUploaded);
  }

  private getCategoryTotalPoints(categoryRequirement: CategoryRequirement): number {
    return (
      this.categoriesInProposal.find((category) => category.code === categoryRequirement.categoryCode)?.totalPoints ?? 0
    );
  }

  submitProposal(): Observable<boolean> {
    if (this.submitInfo()) {
      let submitInfoWithSelections = this.submitInfo()!;
      if (!this.planAllowsChangeExamComponent()) {
        // Use a copy of the submitInfo with only selected minor/other courses
        // (selection checkboxes only apply if placing is done by the SVO)
        submitInfoWithSelections = this.createCopySubmitInfoWithSelections();
      }

      return this.loadingService.present("submitProposal.submittingPlan").pipe(
        switchMap(() =>
          this.submitProposalService.submitProposal(
            submitInfoWithSelections,
            this.filesToUpload,
            this.studentExplanation!,
          ),
        ),
        switchMap((afterSubmitProposalResponse) => {
          if (afterSubmitProposalResponse) {
            if (afterSubmitProposalResponse.statusMessages?.length) {
              this.messages.set(afterSubmitProposalResponse.statusMessages);
              this.loadingService.dismiss();
              return of(false); // Keep the modal open
            } else {
              // submit of proposal was successful
              // If proposal has documents to upload, upload them now
              return this.submitProposalService.uploadDocuments(afterSubmitProposalResponse, this.filesToUpload).pipe(
                tap(() => this.loadingService.dismiss()),
                switchMap(() =>
                  // force reload of the plan details, so that the plan-level proposal details and the submitted course statuses are updated
                  // use switchMap to ensure we wait for getOrFetchPlanDetailsAndPlanLater to finish
                  this.planService.getOrFetchPlanDetailsAndPlanLater(afterSubmitProposalResponse.planId, true).pipe(
                    tap(() => {
                      // To trigger the recalculation of the display of the course menu options,
                      // signalling courseUpdated for each course doesn't work,
                      // because only the last processed course code is picked up in plan-grid-course.
                      // So use a signal that indicates that the plan is replaced with a new one,
                      // with the same courses but updated statuses.
                      this.planActionService.signalNewPlanWithUpdatedStatuses();
                    }),
                  ),
                ),
                tap(() => this.resetState()),
                map(() => true), // Close modal
              );
            }
          }

          return of(false);
        }),
      );
    }

    return of(false);
  }

  /**
   * Return a copy of submitInfo which only includes the selected courses.
   * If not enough points are selected for a minor, the courses of that minor are added as other electives courses.
   * Use only when placing is done by the SVO.
   */
  public createCopySubmitInfoWithSelections(): SubmitInfo {
    // create a deep copy of the submitInfo to avoid modifying the original object
    const submitInfo: SubmitInfo = structuredClone(this.submitInfo()!);

    submitInfo.electives.minors = [];
    submitInfo.electives.courses = [];

    // add all courses from minors that are selected when placing is done by the SVO
    this.minorsInProposal.forEach((minorInProposal) => {
      if (this.planAllowsChangeExamComponent() || minorInProposal.totalPoints >= minorInProposal.minimumPoints) {
        const updatedMinor = this.findMinorAndLimitToSelectedCourses(minorInProposal);
        if (updatedMinor) {
          submitInfo.electives.minors.push(updatedMinor);
        }
      } else {
        // Minor requirements are not met, add (selected) courses as individual (other) courses.
        // Note that minorInProposal.courses are already filtered to only include selected courses.
        submitInfo.electives.courses.push(...minorInProposal.selectedCourses);
      }
    });

    // add all other courses that are selected
    const selectedOtherCourses: SubmitInfoCourse[] = this.submitInfo()!.electives.courses.filter(
      (course: SubmitInfoCourse) => this.isCourseSelected(course),
    );
    if (selectedOtherCourses) {
      submitInfo.electives.courses.push(...selectedOtherCourses);
    }

    return submitInfo;
  }

  private findMinorAndLimitToSelectedCourses(minorInProposal: MinorInProposal): SubmitInfoMinor | undefined {
    const minor = this.submitInfo()?.electives.minors.find((m) => minorInProposal.isEqual(m));

    if (minor !== undefined) {
      // Should never be undefined
      minor.examComponents.forEach((component) => {
        component.courses = component.courses.filter((course) => this.isSelectedMinorCourse(course, minorInProposal));
      });
    }

    return minor;
  }

  private isSelectedMinorCourse(course: SubmitInfoCourse, minorInProposal: MinorInProposal) {
    return minorInProposal.selectedCourses.some((c) => c.id === course.id);
  }

  isCourseSelected(course: SubmitInfoCourse): boolean {
    if (this.planAllowsChangeExamComponent()) {
      return true;
    }
    return this.categoriesInProposal.some((category) => category.selectedCourses.some((c) => c.id === course.id));
  }

  buildInfoTable(): GenericDataListItem[] {
    const submitInfo = this.submitInfo()!;
    const list: GenericDataListItem[] = [];
    const languageCode = this.languageService.getLanguage();
    list.push({
      label: `${submitInfo.electives.examComponentName}`,
      value: "",
      whitespaceBottom: true,
    });
    if (submitInfo.electives.minimumPoints > 0) {
      list.push({
        label: `${this.translate.instant("submitProposal.minCreditsElectives")}:`,
        value: CaciUtil.getStudyPointsDisplay(submitInfo.electives.minimumPoints, languageCode),
        whitespaceLeft: true,
      });
    }
    if (submitInfo.electives.maximumPoints && submitInfo.electives.maximumPoints > 0) {
      list.push({
        label: `${this.translate.instant("submitProposal.maxCreditsElectives")}:`,
        value: CaciUtil.getStudyPointsDisplay(submitInfo.electives.maximumPoints, languageCode),
        whitespaceLeft: true,
        whitespaceBottom: true,
      });
    }
    list.push({
      label: `${this.translate.instant("submitProposal.selectedCreditsElectives")}:`,
      value: CaciUtil.getStudyPointsDisplay(this.selectedElectivesPoints() ?? 0, languageCode),
      whitespaceLeft: true,
    });

    if (submitInfo.electives.categoryRequirements.length) {
      submitInfo.electives.categoryRequirements.forEach((categoryRequirement, index) => {
        list.push({
          label: `${categoryRequirement.categoryDescription}:`,
          value: `${CaciUtil.getStudyPointsDisplay(this.getCategoryTotalPoints(categoryRequirement), languageCode)}`,
          whitespaceLeft: true,
          ...(index === 0 ? { whitespaceTop: true } : {}),
        });
      });
    }

    list.push({
      label: `${this.translate.instant("submitProposal.totaalGepland")}:`,
      value: CaciUtil.getStudyPointsDisplay(this.totalPoints() ?? 0, languageCode),
      whitespaceTop: true,
    });

    return list;
  }

  setMinorsInProposal(submitInfo: SubmitInfo): void {
    this.minorsInProposal = submitInfo.electives.minors.map(
      (minor) => new MinorInProposal(minor.code, minor.name, minor.minimumPoints, minor.type),
    );
  }

  setMinorComponentsInProposal(submitInfo: SubmitInfo): void {
    this.minorComponentsInProposal = submitInfo.electives.minors.flatMap((minor) =>
      minor.examComponents.map((examComponent) => new MinorComponentInProposal(examComponent.id, examComponent.name)),
    );
  }

  private resetState() {
    this.submitInfo.set(undefined);
    this.categoriesInProposal = [];
    this.minorsInProposal = [];
    this.minorComponentsInProposal = [];
    this.messages.set([]);
    this.selectedElectivesPoints.set(0);
    this.totalPoints.set(0);
    this.fromLessThanMinToMaxOrMoreWith1Course = false;
    this.filesToUpload = [];
    this.electivesCourseGroups = [];
    this.otherCourseGroups = [];
    this.planAllowsChangeExamComponent.set(false);
    this.studentExplanation = undefined;
    this.submitDisabled = true;
  }
}
export class CourseListWithTotalPoints {
  selectedCourses: SubmitInfoCourse[] = [];
  totalPoints = 0;

  addCourseToSelection(course: SubmitInfoCourse): void {
    let additionalPoints = 0;
    const found = this.selectedCourses.some((thisCourse: SubmitInfoCourse) => thisCourse.id === course.id);

    if (!found) {
      this.selectedCourses.push(course);
      additionalPoints = course.studyPoints;
    }

    this.totalPoints = CaciUtil.roundStudyPoints(this.totalPoints + additionalPoints);
  }

  removeCourseFromSelection(course: SubmitInfoCourse): void {
    let lossOfPoints = 0;
    const index = this.selectedCourses.findIndex((c) => c.id === course.id);
    if (index !== -1) {
      this.selectedCourses.splice(index, 1);
      lossOfPoints = course.studyPoints;
    }

    this.totalPoints = CaciUtil.roundStudyPoints(this.totalPoints - lossOfPoints);
  }
}

export class CategoryInProposal extends CourseListWithTotalPoints {
  code: string;

  constructor(categoryCode: string) {
    super();
    this.code = categoryCode;
  }
}

export class MinorInProposal extends CourseListWithTotalPoints {
  code: string;
  name: string;
  type: string;
  minimumPoints: number;

  constructor(code: string, name: string, minimumPoints: number, type: string) {
    super();
    this.code = code;
    this.name = name;
    this.minimumPoints = minimumPoints;
    this.type = type;
  }

  isEqual(minor: SubmitInfoMinor): boolean {
    return minor.code === this.code;
  }
}

export class MinorComponentInProposal extends CourseListWithTotalPoints {
  id: number;
  name: string;

  constructor(id: number, name: string) {
    super();
    this.id = id;
    this.name = name;
  }

  isEqual(minorComponent: SubmitInfoMinorExamComponent): boolean {
    return minorComponent.id === this.id;
  }
}
